import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

/**
 * Production-ready PrivateRoute component with enhanced error handling
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @param {string} props.fallbackPath - Custom fallback path (default: "/login")
 * @param {boolean} props.requireVerified - Whether to require email verification
 * @returns {React.ReactNode} - Either the protected component or redirect
 */
const PrivateRoute = ({ 
  children, 
  fallbackPath = "/login",
  requireVerified = false 
}) => {
  const location = useLocation();
  const { isAuthenticated, loading, user, error } = useSelector(state => state.auth);

  // Handle loading state with timeout awareness
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen" style={{ background: "#09090f" }}>
        <div className="text-center">
          <div
            className="w-10 h-10 rounded-full mx-auto"
            style={{
              animation: "stocker-ring-spin 0.9s linear infinite",
              background: "conic-gradient(from 0deg, #a78bfa, #818cf8, #60a5fa, transparent 100%)",
              WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
            }}
          />
          <p className="mt-3 text-sm" style={{ color: "#94a3b8" }}>Verifying access...</p>
          {error && (
            <p className="mt-1 text-xs" style={{ color: "#f87171" }}>
              Authentication check failed. Redirecting...
            </p>
          )}
        </div>
      </div>
    );
  }

  // Handle authentication errors
  if (error && !isAuthenticated) {
    return (
      <Navigate
        to={fallbackPath}
        replace
        state={{ 
          from: location.pathname + location.search,
          error: "Authentication error occurred. Please log in again."
        }}
      />
    );
  }

  // Check authentication status
  if (!isAuthenticated) {
    // Prevent infinite redirect loops
    if (location.pathname === fallbackPath) {
      return children;
    }

    return (
      <Navigate
        to={fallbackPath}
        replace
        state={{ 
          from: location.pathname + location.search,
          message: "Please log in to access this page."
        }}
      />
    );
  }

  // Optional: Check for email verification requirement
  if (requireVerified && user && !user.is_verified) {
    return (
      <Navigate
        to="/verify-email"
        replace
        state={{ 
          from: location.pathname + location.search,
          message: "Please verify your email address to access this page."
        }}
      />
    );
  }

  // User is authenticated and verified (if required)
  return children;
};

export default PrivateRoute;